import { useState, useCallback } from "react";
import { ONBOARDING_STAGES, ALL_ONBOARDING_DATA_POINTS } from "@/data/onboardingData";
import { ALL_HOMEBUILDER_DATA_POINTS } from "@/data/homebuilderData";

const ALL_DATA_POINTS = [...ALL_ONBOARDING_DATA_POINTS, ...ALL_HOMEBUILDER_DATA_POINTS];

const STORAGE_KEY = "fordje-onboarding-selections";

export function useOnboardingState(preSelectAll = false) {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => {
    if (preSelectAll) {
      return new Set(ALL_DATA_POINTS.map(dp => dp.key));
    }
    return new Set<string>();
  });

  const toggle = useCallback((key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const visitedStagesRef = useState(() => new Set<string>())[0];
  const ensureTypicalForStage = useCallback((stageId: string, stageKeys: string[]) => {
    if (visitedStagesRef.has(stageId)) return;
    visitedStagesRef.add(stageId);
    const typicalSet = ALL_DATA_POINTS
      .filter(dp => dp.typical && stageKeys.includes(dp.key))
      .map(dp => dp.key);
    setSelectedKeys(prev => {
      const next = new Set(prev);
      typicalSet.forEach(k => next.add(k));
      return next;
    });
  }, [visitedStagesRef]);

  const reinitialize = useCallback((_industryId?: string) => {
    visitedStagesRef.clear();
    setSelectedKeys(new Set<string>());
  }, [visitedStagesRef]);

  const persist = useCallback(() => {
    const arr = Array.from(selectedKeys);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
  }, [selectedKeys]);

  const count = selectedKeys.size;

  const getSelectedLabels = useCallback(() => {
    return ALL_DATA_POINTS
      .filter(dp => selectedKeys.has(dp.key))
      .map(dp => dp.label);
  }, [selectedKeys]);

  return { selectedKeys, toggle, count, persist, getSelectedLabels, reinitialize, ensureTypicalForStage };
}

export function getPersistedOnboardingSelections(): string[] | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}
